import { Modal } from '../../components/Modal';
import { countChanges, isEmpty, type DraftChanges } from './draftChanges';

interface SubmitConfirmModalProps {
  changes: DraftChanges | null;

  /** True when there is nothing on file yet, so there is nothing to compare against. */
  firstSubmission: boolean;

  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The last look before an enrollment goes back to HR.
 *
 * Everything on this form can be edited freely, and an edit costs HR a re-review — so the point of
 * the dialog is to make the difference between "I fixed a typo" and "I changed who is enrolled"
 * impossible to miss. Additions, edits and removals are separated and colour-coded rather than listed
 * as one run of sentences.
 */
export function SubmitConfirmModal({
  changes,
  firstSubmission,
  busy,
  onConfirm,
  onCancel,
}: SubmitConfirmModalProps) {
  if (!changes) {
    return null;
  }

  const nothingChanged = !firstSubmission && isEmpty(changes);

  return (
    <Modal
      open
      wide
      title={firstSubmission ? 'Submit your enrollment' : 'Review your changes'}
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onCancel} disabled={busy}>
            Keep editing
          </button>
          <button type="button" className="button button--primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Sending…' : firstSubmission ? 'Submit for HR review' : 'Send for review'}
          </button>
        </>
      }
    >
      {firstSubmission ? (
        <p className="care-confirm__lead">
          HR will validate the form and the documents you attached. Your care starts once they approve
          it — the clinic calls you to schedule the first appointments.
        </p>
      ) : nothingChanged ? (
        <div className="issues__block issues__block--warning">
          <p className="issues__title">Nothing has changed</p>
          <p>
            This matches the version you opened. Sending it puts it back in HR's queue for no reason —
            keep editing, or send it anyway if you meant to.
          </p>
        </div>
      ) : (
        <>
          <p className="care-confirm__lead">
            {countChanges(changes)} change{countChanges(changes) === 1 ? '' : 's'} compared with the
            version you opened. Sending puts the enrollment back to HR for review.
          </p>

          {changes.header.length > 0 && (
            <ChangeGroup kind="changed" title="Your answers">
              {changes.header.map((c) => (
                <li key={c.field}>
                  <span className="care-diff__field">{c.field}</span>
                  <FromTo from={c.from} to={c.to} />
                </li>
              ))}
            </ChangeGroup>
          )}

          {changes.added.length > 0 && (
            <ChangeGroup kind="added" title={`Added (${changes.added.length})`}>
              {changes.added.map((who) => (
                <li key={who}>{who}</li>
              ))}
            </ChangeGroup>
          )}

          {changes.changed.length > 0 && (
            <ChangeGroup kind="changed" title={`Edited (${changes.changed.length})`}>
              {changes.changed.map((entry) => (
                <li key={entry.who}>
                  <span className="care-diff__field">{entry.who}</span>
                  <ul className="care-diff__fields">
                    {entry.fields.map((f) => (
                      <li key={f.field}>
                        {f.field}: <FromTo from={f.from} to={f.to} />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ChangeGroup>
          )}

          {changes.removed.length > 0 && (
            <ChangeGroup kind="removed" title={`Removed (${changes.removed.length})`}>
              {changes.removed.map((who) => (
                <li key={who}>
                  {who}
                  <span className="care-diff__note">their document is deleted too</span>
                </li>
              ))}
            </ChangeGroup>
          )}
        </>
      )}
    </Modal>
  );
}

function ChangeGroup({
  kind,
  title,
  children,
}: {
  kind: 'added' | 'changed' | 'removed';
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`care-diff care-diff--${kind}`}>
      <h3 className="care-diff__title">{title}</h3>
      <ul className="care-diff__list">{children}</ul>
    </section>
  );
}

function FromTo({ from, to }: { from: string; to: string }) {
  return (
    <span className="care-diff__value">
      <s className="care-diff__from">{from}</s>
      <span aria-hidden="true"> → </span>
      <strong className="care-diff__to">{to}</strong>
    </span>
  );
}
